import type { SupabaseClient } from "@supabase/supabase-js";
import type { CrmDataProvider } from "@/components/atomic-crm/providers/types";
import type { ModuleId } from "@/modules/registry";

/**
 * App store content and setup guides for every module (Apps page,
 * /apps/:moduleId landing pages and the setup dialog). Screenshots live in
 * public/apps/<module>.jpg and come from the test companies.
 */

export type SetupCheckContext = {
  supabase: SupabaseClient;
  dataProvider: CrmDataProvider;
};

export type ModuleSetupStep = {
  id: string;
  title: string;
  description: string;
  /** Where the member does it. */
  to: string;
  actionLabel: string;
  /** Nice to have; does not block "ready". */
  optional?: boolean;
  /** True when done. Throwing counts as "can't tell" (shown as to do). */
  check: (ctx: SetupCheckContext) => Promise<boolean>;
};

export type ModuleWorksWith = { moduleId: ModuleId; how: string };

export type ModuleCatalogEntry = {
  tagline: string;
  /** One line for store cards. */
  summary: string;
  benefits: Array<{ title: string; description: string }>;
  worksWith: ModuleWorksWith[];
  screenshot: string;
  setup: ModuleSetupStep[];
};

const settings = (tab: string, section?: string) =>
  `/settings?tab=${tab}${section ? `&section=${section}` : ""}`;

/** At least one row the member can see (RLS scopes to their company). */
const hasRows =
  (table: string, apply?: (query: any) => any) =>
  async ({ supabase }: SetupCheckContext) => {
    let query = supabase
      .from(table)
      .select("*", { count: "exact", head: true });
    if (apply) query = apply(query);
    const { count, error } = await query;
    if (error) throw error;
    return (count ?? 0) > 0;
  };

const companyProfileFilled = async ({ supabase }: SetupCheckContext) => {
  const { data, error } = await supabase
    .from("organizations")
    .select("email, phone")
    .maybeSingle();
  if (error) throw error;
  return Boolean(data?.email?.trim() || data?.phone?.trim());
};

const senderEmailReady = async ({ dataProvider }: SetupCheckContext) =>
  (await dataProvider.getEmailDeliverySettings()).configured === true;

const smsReady = async ({ dataProvider }: SetupCheckContext) => {
  const settings = await dataProvider.getMessagingSettings();
  return settings.sms_enabled === true;
};

const voiceReady = async ({ dataProvider }: SetupCheckContext) =>
  (await dataProvider.getMessagingSettings()).voice_enabled === true;

const stripeReady = async ({ dataProvider }: SetupCheckContext) =>
  (await dataProvider.getStripeClientSettings()).configured === true;

const timezoneSet = async ({ supabase }: SetupCheckContext) => {
  const { data, error } = await supabase
    .from("configuration")
    .select("config")
    .maybeSingle();
  if (error) throw error;
  const config = (data?.config ?? {}) as { companyTimezone?: string };
  return Boolean(config.companyTimezone?.trim());
};

const SENDER_STEP: ModuleSetupStep = {
  id: "sender-email",
  title: "Set up your sender email",
  description:
    "Emails to your clients go out from your own address and domain.",
  to: settings("connectors", "mail"),
  actionLabel: "Email settings",
  check: senderEmailReady,
};

export const MODULE_CATALOG: Record<ModuleId, ModuleCatalogEntry> = {
  accounts: {
    tagline: "Every client and every person, in one place.",
    summary: "Clients, people and their history.",
    benefits: [
      {
        title: "One record per client",
        description:
          "Contact details, notes, tasks, emails and files on a single page.",
      },
      {
        title: "Timeline of everything",
        description:
          "See every call, email, ticket and meeting with a client in order.",
      },
      {
        title: "People only, if that's you",
        description:
          "Work with a simple list of people when you don't sell to companies.",
      },
    ],
    worksWith: [
      { moduleId: "tickets", how: "Client tickets show on their record." },
      { moduleId: "calendar", how: "Meetings appear on the client timeline." },
      { moduleId: "invoices", how: "Invoices and payments per client." },
      { moduleId: "messages", how: "Text and call clients from their page." },
    ],
    screenshot: "/apps/accounts.jpg",
    setup: [
      {
        id: "company-profile",
        title: "Fill in your company profile",
        description:
          "Your name, phone and email appear on forms, emails and invoices.",
        to: settings("company"),
        actionLabel: "Company profile",
        check: companyProfileFilled,
      },
      {
        id: "first-contact",
        title: "Add your first contact",
        description: "Add a client by hand or import a CSV.",
        to: "/accounts?create=contact",
        actionLabel: "New contact",
        check: hasRows("contacts"),
      },
    ],
  },
  tasks: {
    tagline: "Never miss a follow-up.",
    summary: "To-dos with due dates and reminders.",
    benefits: [
      {
        title: "Due dates and reminders",
        description: "Get reminded before a call or a visit is due.",
      },
      {
        title: "Assign to your team",
        description: "Hand work to teammates and see what's overdue.",
      },
      {
        title: "Linked to clients",
        description: "Every task can live on a client record.",
      },
    ],
    worksWith: [
      { moduleId: "accounts", how: "Tasks show on client records." },
      { moduleId: "calendar", how: "Meetings and tasks share your day." },
    ],
    screenshot: "/apps/tasks.jpg",
    setup: [
      {
        id: "first-task",
        title: "Create your first task",
        description: "Add a follow-up with a due date.",
        to: "/tasks",
        actionLabel: "Open Tasks",
        check: hasRows("tasks"),
      },
    ],
  },
  pipeline: {
    tagline: "See every deal from first call to finished job.",
    summary: "Leads pipeline and project boards.",
    benefits: [
      {
        title: "Pipeline board",
        description: "Drag leads through your stages and see what's stuck.",
      },
      {
        title: "Projects after the sale",
        description: "Track work, milestones, costs and change orders.",
      },
      {
        title: "Your own stages",
        description: "Name the steps the way your business works.",
      },
    ],
    worksWith: [
      { moduleId: "accounts", how: "Leads and projects belong to clients." },
      { moduleId: "proposals", how: "Send proposals from a lead." },
      { moduleId: "invoices", how: "Bill a project when it's done." },
    ],
    screenshot: "/apps/pipeline.jpg",
    setup: [
      {
        id: "stages",
        title: "Review your pipeline stages",
        description: "Rename or add the steps your deals go through.",
        to: settings("workflows", "pipelines"),
        actionLabel: "Pipeline settings",
        check: hasRows("organization_pipeline_stages"),
      },
      {
        id: "first-project",
        title: "Create your first project",
        description: "Start tracking a job or a deal.",
        to: "/deals",
        actionLabel: "Open Projects",
        check: hasRows("deals"),
      },
    ],
  },
  calendar: {
    tagline: "Book, remind and show up on time.",
    summary: "Meetings, booking links and reminders.",
    benefits: [
      {
        title: "Meetings with reminders",
        description: "Clients get invites and reminders automatically.",
      },
      {
        title: "Booking links",
        description: "Let clients pick a time that works for you.",
      },
      {
        title: "Your week at a glance",
        description: "Meetings and tasks together on the dashboard.",
      },
    ],
    worksWith: [
      { moduleId: "accounts", how: "Meetings appear on client timelines." },
      { moduleId: "tasks", how: "Plan the day with tasks and meetings." },
    ],
    screenshot: "/apps/calendar.jpg",
    setup: [
      {
        id: "timezone",
        title: "Set your workspace timezone",
        description: "Meeting times and reminders use this timezone.",
        to: settings("company"),
        actionLabel: "Company profile",
        check: timezoneSet,
      },
      SENDER_STEP,
      {
        id: "first-meeting",
        title: "Schedule your first meeting",
        description: "Invite a client and send them a calendar invite.",
        to: "/meetings",
        actionLabel: "Open Meeting",
        check: hasRows("calendar_events"),
      },
    ],
  },
  tickets: {
    tagline: "Every client email becomes a case you can track.",
    summary: "Shared support inbox with replies and templates.",
    benefits: [
      {
        title: "One shared inbox",
        description:
          "Client emails turn into tickets your whole team can answer.",
      },
      {
        title: "Replies with your signature",
        description: "Answer from Sigma; clients see your address and brand.",
      },
      {
        title: "Templates and statuses",
        description: "Reply faster and know what's open, waiting or solved.",
      },
    ],
    worksWith: [
      { moduleId: "accounts", how: "Tickets show on the client's record." },
      { moduleId: "invoices", how: "Bill a ticket's work in one step." },
    ],
    screenshot: "/apps/tickets.jpg",
    setup: [
      {
        id: "inbox",
        title: "Connect your support inbox",
        description:
          "Choose the address clients write to and forward it to Sigma.",
        to: settings("tickets", "outbound"),
        actionLabel: "Inbox settings",
        check: hasRows("ticket_inboxes", (q) => q.eq("is_active", true)),
      },
      SENDER_STEP,
      {
        id: "signature",
        title: "Add your reply signature",
        description: "Your name, title and contact under every reply.",
        to: settings("tickets", "signature"),
        actionLabel: "Signature",
        check: hasRows("ticket_inboxes", (q) =>
          q.not("reply_signature_html", "is", null),
        ),
      },
    ],
  },
  messages: {
    tagline: "Text and call clients from your business number.",
    summary: "SMS and calls with clients.",
    benefits: [
      {
        title: "Two-way texting",
        description: "Clients text your business number; your team replies.",
      },
      {
        title: "Calls from the browser",
        description: "Call clients and keep a record on their page.",
      },
      {
        title: "Shared conversations",
        description: "Everyone sees the full thread with a client.",
      },
    ],
    worksWith: [
      { moduleId: "accounts", how: "Text or call from any client record." },
      { moduleId: "marketing", how: "Send text campaigns." },
    ],
    screenshot: "/apps/messages.jpg",
    setup: [
      {
        id: "sms-provider",
        title: "Connect Twilio or Telnyx",
        description: "Add your account and business phone number for texts.",
        to: settings("connectors", "twilio"),
        actionLabel: "SMS settings",
        check: smsReady,
      },
      {
        id: "voice",
        title: "Turn on calling",
        description: "Make and receive calls in the browser.",
        to: settings("connectors", "twilio"),
        actionLabel: "Voice settings",
        optional: true,
        check: voiceReady,
      },
    ],
  },
  mail: {
    tagline: "Your inbox, next to your clients.",
    summary: "Your Gmail or Outlook inbox inside Sigma.",
    benefits: [
      {
        title: "Gmail and Outlook",
        description: "Read and send email without leaving Sigma.",
      },
      {
        title: "Linked to clients",
        description: "Emails show on the right client's timeline.",
      },
      {
        title: "Shared mailboxes",
        description: "Share an inbox like info@ with your team.",
      },
    ],
    worksWith: [
      { moduleId: "accounts", how: "Emails attach to client timelines." },
    ],
    screenshot: "/apps/mail.jpg",
    setup: [
      {
        id: "mailbox",
        title: "Connect a mailbox",
        description: "Sign in with Google or Microsoft, or connect by IMAP.",
        to: settings("connectors", "mailboxes"),
        actionLabel: "Mailboxes",
        check: hasRows("mail_accounts_safe"),
      },
    ],
  },
  invoices: {
    tagline: "Get paid faster, by card or bank.",
    summary: "Invoices, payments and subscriptions.",
    benefits: [
      {
        title: "Professional invoices",
        description: "Branded invoices with a pay-online link.",
      },
      {
        title: "Card and bank payments",
        description: "Clients pay through Stripe; payments record themselves.",
      },
      {
        title: "Reminders and receipts",
        description: "Automatic reminders and receipts after each payment.",
      },
    ],
    worksWith: [
      { moduleId: "accounts", how: "Invoices and payments per client." },
      { moduleId: "tickets", how: "Bill the work on a ticket." },
      { moduleId: "pipeline", how: "Bill a project." },
    ],
    screenshot: "/apps/invoices.jpg",
    setup: [
      {
        id: "stripe",
        title: "Connect Stripe",
        description: "Your own Stripe account receives your clients' payments.",
        to: settings("connectors", "stripe"),
        actionLabel: "Stripe settings",
        check: stripeReady,
      },
      SENDER_STEP,
      {
        id: "first-invoice",
        title: "Create your first invoice",
        description: "Bill a client and send the payment link.",
        to: "/billing",
        actionLabel: "Open Invoices",
        check: hasRows("client_invoices"),
      },
    ],
  },
  proposals: {
    tagline: "Proposals clients can read, sign and pay.",
    summary: "Proposals with e-signature and deposits.",
    benefits: [
      {
        title: "Good-looking proposals",
        description: "Build them from your services and send a link.",
      },
      {
        title: "Sign and pay online",
        description: "Clients accept, sign and pay a deposit in one go.",
      },
      {
        title: "Know when they read it",
        description: "See when a proposal is opened and accepted.",
      },
    ],
    worksWith: [
      { moduleId: "pipeline", how: "Proposals move leads forward." },
      { moduleId: "invoices", how: "Deposits and payments are recorded." },
    ],
    screenshot: "/apps/proposals.jpg",
    setup: [
      {
        id: "products",
        title: "Add your services",
        description: "The services and prices you put in proposals.",
        to: settings("products"),
        actionLabel: "Products",
        check: hasRows("service_packages"),
      },
      {
        id: "first-proposal",
        title: "Create your first proposal",
        description: "Pick a client and the services to offer.",
        to: "/billing?tab=proposals",
        actionLabel: "Open Proposals",
        check: hasRows("proposals"),
      },
    ],
  },
  contracts: {
    tagline: "Agreements signed online, kept with the client.",
    summary: "Contracts and signed agreements.",
    benefits: [
      {
        title: "Your terms, reused",
        description: "Write your terms once and use them on every contract.",
      },
      {
        title: "E-signature",
        description: "Clients sign from their phone or computer.",
      },
      {
        title: "Kept with the client",
        description: "Signed copies stay on the client's record.",
      },
    ],
    worksWith: [
      { moduleId: "accounts", how: "Contracts live on client records." },
      { moduleId: "proposals", how: "Terms attach to proposals." },
    ],
    screenshot: "/apps/contracts.jpg",
    setup: [
      {
        id: "terms",
        title: "Write your contract terms",
        description: "The legal text your contracts use.",
        to: settings("proposals"),
        actionLabel: "Contract terms",
        check: hasRows("organization_contract_terms"),
      },
      {
        id: "first-contract",
        title: "Create your first contract",
        description: "Send a contract to a client to sign.",
        to: "/contracts",
        actionLabel: "Open Contracts",
        check: hasRows("contracts"),
      },
    ],
  },
  marketing: {
    tagline: "Stay in touch with every client at once.",
    summary: "Email and SMS campaigns.",
    benefits: [
      {
        title: "Email campaigns",
        description: "Send news and offers to a list of clients.",
      },
      {
        title: "Text campaigns",
        description: "Reach clients by SMS from your business number.",
      },
      {
        title: "Audiences from your CRM",
        description: "Target clients by status, tags or service.",
      },
    ],
    worksWith: [
      { moduleId: "accounts", how: "Audiences come from your clients." },
      { moduleId: "messages", how: "Text campaigns use your SMS number." },
    ],
    screenshot: "/apps/marketing.jpg",
    setup: [
      SENDER_STEP,
      {
        id: "sms",
        title: "Connect SMS for text campaigns",
        description: "Only needed if you send text campaigns.",
        to: settings("connectors", "twilio"),
        actionLabel: "SMS settings",
        optional: true,
        check: smsReady,
      },
    ],
  },
  forms: {
    tagline: "Turn website visitors into leads and requests.",
    summary: "Public forms that create leads and requests.",
    benefits: [
      {
        title: "Ready-made forms",
        description: "Contact, quote request, surveys and more.",
      },
      {
        title: "Straight into Sigma",
        description: "Each submission creates a contact or a request.",
      },
      {
        title: "Your branding",
        description: "Your logo and colors on every form.",
      },
    ],
    worksWith: [
      { moduleId: "accounts", how: "Submissions create contacts." },
      { moduleId: "pipeline", how: "Quote requests become leads." },
    ],
    screenshot: "/apps/forms.jpg",
    setup: [
      {
        id: "first-form",
        title: "Create your first form",
        description: "Pick a template and share the link.",
        to: settings("forms"),
        actionLabel: "Forms",
        check: hasRows("form_instances"),
      },
    ],
  },
  reports: {
    tagline: "Know how the business is doing.",
    summary: "Reports across your modules.",
    benefits: [
      {
        title: "Revenue and payments",
        description: "What came in, what's pending and what's late.",
      },
      {
        title: "Team activity",
        description: "Tickets solved, tasks done and response times.",
      },
      {
        title: "Grows with your apps",
        description: "Reports appear for the modules you use.",
      },
    ],
    worksWith: [
      { moduleId: "invoices", how: "Revenue and payment reports." },
      { moduleId: "tickets", how: "Support workload and response times." },
    ],
    screenshot: "/apps/reports.jpg",
    setup: [],
  },
  web_monitor: {
    tagline: "Know when a client's website goes down.",
    summary: "Website uptime and audit reports.",
    benefits: [
      {
        title: "Uptime alerts",
        description: "Get told when a site stops responding.",
      },
      {
        title: "Audit reports",
        description: "Speed and SEO reports you can share with clients.",
      },
      {
        title: "Per client",
        description: "Each website belongs to a client.",
      },
    ],
    worksWith: [{ moduleId: "accounts", how: "Websites belong to clients." }],
    screenshot: "/apps/web_monitor.jpg",
    setup: [
      {
        id: "first-site",
        title: "Add a website to monitor",
        description: "Enter a client's website address.",
        to: "/web-monitor",
        actionLabel: "Open Web Monitor",
        check: hasRows("monitored_websites"),
      },
    ],
  },
  hostinger: {
    tagline: "Domains and hosting, next to your clients.",
    summary: "Domains and hosting from Hostinger.",
    benefits: [
      {
        title: "All your domains",
        description: "See every domain and when it renews.",
      },
      {
        title: "Linked to clients",
        description: "Know which client each domain belongs to.",
      },
      {
        title: "Weekly sync",
        description: "Sigma keeps the list up to date for you.",
      },
    ],
    worksWith: [
      { moduleId: "accounts", how: "Domains belong to clients." },
      { moduleId: "web_monitor", how: "Monitor the sites you host." },
    ],
    screenshot: "/apps/hostinger.jpg",
    setup: [
      {
        id: "api-key",
        title: "Connect your Hostinger API key",
        description: "From your Hostinger account, under API.",
        to: settings("connectors", "hostinger"),
        actionLabel: "Hostinger settings",
        check: async ({ dataProvider }) =>
          (await dataProvider.getHostingerSettings()).has_api_token === true,
      },
    ],
  },
  assistant: {
    tagline: "Ask your CRM anything.",
    summary: "AI assistant over your CRM data.",
    benefits: [
      {
        title: "Answers from your data",
        description: "Ask who owes you, what's due, or what a client said.",
      },
      {
        title: "Drafts for you",
        description: "Draft replies, follow-ups and summaries.",
      },
      {
        title: "Nothing to set up",
        description: "Works with the modules you already use.",
      },
    ],
    worksWith: [
      { moduleId: "accounts", how: "Knows your clients and history." },
      { moduleId: "tickets", how: "Summarizes and drafts replies." },
    ],
    screenshot: "/apps/assistant.jpg",
    setup: [],
  },
};
